// Skysight bundle inline style (decoded from live DOM, 2026-05-26):
//   .fixed-cape-area { fill: rgba(255, 0, 0, 0.05); stroke: none; }
//   .fixed-cin-area  { fill: rgba(0, 0, 255, 0.05); stroke: none; }
//   .fixed-cape-label { fill: rgba(139,0,69,0.4);  font-size 14px bold; centered }
//   .fixed-cin-label  { fill: rgba(0,0,139,0.4);   font-size 14px bold; centered }
const CAPE_FILL  = 'rgba(255, 0, 0, 0.05)';
const CIN_FILL   = 'rgba(0, 0, 255, 0.05)';
const CAPE_LABEL = 'rgba(139, 0, 69, 0.4)';
const CIN_LABEL  = 'rgba(0, 0, 139, 0.4)';

// Skysight CAPE-label gate constants (decoded from bundle):
//   S = 30  → required width AT the polygon's vertical middle (horizontal slice)
//   X = 50  → required gap between the label y and the LFC y, in px
const LABEL_MIN_WIDTH = 30;
const LFC_GAP_MIN = 50;

/**
 * Draw CAPE/CIN polygons + at-most-one CAPE label per polygon, faithfully
 * matching Skysight's algorithm (docs/skysight-skewt-bundle.js):
 *
 *   1. Compute LFC pressure (`h`); if none, no CAPE labels are drawn.
 *   2. For each CAPE polygon whose MEAN pressure < LFC pressure (i.e. polygon
 *      lies ABOVE the LFC):
 *      a. k = (min(vertex.y) + max(vertex.y)) / 2  — vertical middle.
 *      b. Find x-positions where polygon EDGES cross y=k → array L.
 *      c. If L.length >= 2: T = L[last] - L[0], B = (L[0] + L[last]) / 2.
 *         Else fallback: T = bbox width, B = vertex centroid x.
 *      d. X = y(LFC) - k.
 *      e. If T >= 30 AND X >= 50 → draw "CAPE" at (B, k).
 *
 * @param {import('d3-selection').Selection} container
 * @param {import('../skewt/scales.js').Scales} scales
 * @param {Array<{type, parcelPath, envPath}>} segments
 * @param {{lfcPressure?: number|null}} [opts]
 */
export function drawCAPECINAreas(container, scales, segments, opts = {}) {
    container.selectAll('.skewt-ext-cape-cin').remove();

    if (!segments || segments.length === 0) return;

    const layer = container.append('g').attr('class', 'skewt-ext-cape-cin');

    // Draw polygon fills + collect per-segment data for label evaluation.
    const capeSegs = [];   // { vertsScreen, meanPress }
    const cinSegs = [];    // same shape, for CIN label
    for (const seg of segments) {
        if (seg.parcelPath.length < 2) continue;
        const ptsP = [
            ...seg.parcelPath,
            ...seg.envPath.slice().reverse(),
        ];
        const vertsScreen = ptsP.map(({ t, p }) => ({
            x: scales.skewX(t, p),
            y: scales.y(p),
        }));
        const polyPoints = vertsScreen.map((v) => `${v.x},${v.y}`).join(' ');
        const isCape = seg.type === 'CAPE';
        layer
            .append('polygon')
            .attr('points', polyPoints)
            .attr('fill', isCape ? CAPE_FILL : CIN_FILL)
            .attr('stroke', 'none')
            .attr('pointer-events', 'none');

        const meanPress = ptsP.reduce((acc, p) => acc + p.p, 0) / ptsP.length;
        (isCape ? capeSegs : cinSegs).push({ vertsScreen, meanPress });
    }

    // CAPE label gate requires LFC. Skysight: `if (h) { ... }`.
    const lfcP = Number.isFinite(opts.lfcPressure) ? opts.lfcPressure : null;
    if (!lfcP) return;

    const yLFC = scales.y(lfcP);

    /** Skysight CAPE-label position+gate computation for one polygon's
     *  on-screen vertex list. Returns null if the gate fails. */
    const computeLabel = (verts) => {
        let minY = Infinity, maxY = -Infinity;
        let sumX = 0, minX = Infinity, maxX = -Infinity;
        for (const v of verts) {
            if (v.y < minY) minY = v.y;
            if (v.y > maxY) maxY = v.y;
            if (v.x < minX) minX = v.x;
            if (v.x > maxX) maxX = v.x;
            sumX += v.x;
        }
        const k = (minY + maxY) / 2;
        // Find x where each edge crosses y = k
        const L = [];
        for (let i = 0; i < verts.length; i++) {
            const A = verts[i];
            const E = verts[(i + 1) % verts.length];
            const crosses =
                (A.y <= k && E.y >= k) || (A.y >= k && E.y <= k);
            if (crosses && Math.abs(E.y - A.y) > 0.001) {
                const xCross = A.x + ((k - A.y) / (E.y - A.y)) * (E.x - A.x);
                L.push(xCross);
            }
        }
        L.sort((a, b) => a - b);
        let B;
        let T = 0;
        if (L.length >= 2) {
            const lo = L[0];
            const hi = L[L.length - 1];
            T = hi - lo;
            B = (lo + hi) / 2;
        } else {
            B = sumX / verts.length;
            T = maxX - minX;
        }
        const X = yLFC - k; // distance from LFC y down to label y
        if (T < LABEL_MIN_WIDTH) return null;
        if (X < LFC_GAP_MIN) return null;
        return { B, k };
    };

    // CAPE labels — one per qualifying polygon (polygon must be above LFC,
    // i.e. mean pressure < LFC pressure).
    for (const seg of capeSegs) {
        if (seg.meanPress >= lfcP) continue; // not above LFC → skip
        const pos = computeLabel(seg.vertsScreen);
        if (!pos) continue;
        layer
            .append('text')
            .attr('class', 'fixed-cape-label')
            .attr('x', pos.B)
            .attr('y', pos.k)
            .style('fill', CAPE_LABEL)
            .style('font-size', '14px')
            .style('font-weight', 'bold')
            .style('text-anchor', 'middle')
            .style('dominant-baseline', 'central')
            .style('pointer-events', 'none')
            .style('user-select', 'none')
            .text('CAPE');
    }

    // CIN labels — same approach but Skysight uses S=25 and no LFC-gap check.
    for (const seg of cinSegs) {
        let minY = Infinity, maxY = -Infinity;
        let sumX = 0, minX = Infinity, maxX = -Infinity;
        for (const v of seg.vertsScreen) {
            if (v.y < minY) minY = v.y;
            if (v.y > maxY) maxY = v.y;
            if (v.x < minX) minX = v.x;
            if (v.x > maxX) maxX = v.x;
            sumX += v.x;
        }
        const k = (minY + maxY) / 2;
        const L = [];
        for (let i = 0; i < seg.vertsScreen.length; i++) {
            const A = seg.vertsScreen[i];
            const E = seg.vertsScreen[(i + 1) % seg.vertsScreen.length];
            const crosses =
                (A.y <= k && E.y >= k) || (A.y >= k && E.y <= k);
            if (crosses && Math.abs(E.y - A.y) > 0.001) {
                const xCross = A.x + ((k - A.y) / (E.y - A.y)) * (E.x - A.x);
                L.push(xCross);
            }
        }
        L.sort((a, b) => a - b);
        let B;
        let T = 0;
        if (L.length >= 2) {
            const lo = L[0];
            const hi = L[L.length - 1];
            T = hi - lo;
            B = (lo + hi) / 2;
        } else {
            B = sumX / seg.vertsScreen.length;
            T = maxX - minX;
        }
        if (T < 25) continue;
        layer
            .append('text')
            .attr('class', 'fixed-cin-label')
            .attr('x', B)
            .attr('y', k)
            .style('fill', CIN_LABEL)
            .style('font-size', '14px')
            .style('font-weight', 'bold')
            .style('text-anchor', 'middle')
            .style('dominant-baseline', 'middle')
            .style('pointer-events', 'none')
            .style('user-select', 'none')
            .text('CIN');
    }
}
